import { isObjectEmpty } from '@creator/ui/utils/object-utils';
import { getCloudRunBaseUrl } from '@src/config';
import type { LinkMeta } from '@src/types/link-meta';
import { useCallback, useRef, useState } from 'react';

export interface UseLinkMetaPayload {
    initialLinkMeta?: LinkMeta;
    onSuccess?: (linkMeta: LinkMeta) => void;
    onError?: (error: Error) => void;
}

const useLinkMeta = (payload: UseLinkMetaPayload) => {
    const { initialLinkMeta = null } = payload;

    const [linkMeta, setLinkMeta] = useState<LinkMeta | null>(initialLinkMeta);
    const [status, setStatus] = useState<'idle' | 'pending' | 'success' | 'error'>('idle');
    const abortControllerRef = useRef<AbortController | null>(null);

    const fetchLinkMeta = useCallback(
        async (url: string) => {
            setStatus('pending');
            abortControllerRef.current = new AbortController(); // Create a new AbortController
            const { signal } = abortControllerRef.current; // Get the signal from the controller

            try {
                const linkToolEndpoint = `${getCloudRunBaseUrl()}/support/linktool`;
                const linkToolRes = await fetch(`${linkToolEndpoint}?url=${encodeURIComponent(url)}`, { signal });

                if (!linkToolRes.ok) throw new Error('Failed to fetch link meta');

                const articleData = await linkToolRes.json();
                if (!articleData.meta) throw new Error('No meta data found');
                const linkMeta = { url, ...articleData.meta } as LinkMeta;
                setLinkMeta(linkMeta);
                if (isObjectEmpty(linkMeta.image || {})) linkMeta.image = undefined;

                setStatus('success');
                payload.onSuccess?.(linkMeta);
            } catch (error) {
                if (signal.aborted) {
                    console.log('Fetch request was canceled');
                } else {
                    console.error('Error fetching link meta:', error);
                    setLinkMeta(null);
                    setStatus('error');
                    payload.onError?.(error as Error);
                }
            }
        },
        [payload]
    );

    const resetLinkMeta = useCallback(() => {
        cancelFetch();
        setLinkMeta(null);
        setStatus('idle');
    }, []);

    const cancelFetch = useCallback(() => {
        if (!abortControllerRef.current) return;

        abortControllerRef.current.abort();
    }, []);

    return { linkMeta, status, fetchLinkMeta, resetLinkMeta, cancelFetch };
};

export default useLinkMeta;
